import React from 'react';
import {
  HiOutlineServer, HiOutlineTrash,
  HiOutlineChevronRight, HiOutlineChevronDown
} from 'react-icons/hi';
import { formatDate } from '../../shared/constants';

function ServicesTab({ product, expandedServices, serviceInfoCache, loadingServiceInfo, toggleService, handleRemoveService }) {
  if (!product.cloudRuns || product.cloudRuns.length === 0) {
    return (
      <div className="services-tab">
        <div className="empty-state">
          <HiOutlineServer size={48} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3>No services attached</h3>
          <p>Add Cloud Run services to this product to start monitoring</p>
        </div>
      </div>
    );
  }

  return (
    <div className="services-tab">
      <div className="services-accordion">
        {product.cloudRuns.map((cr, index) => {
          const key = `svc-${index}`;
          const isExpanded = expandedServices[key];
          const info = serviceInfoCache[key];
          const isLoadingInfo = loadingServiceInfo[key];

          return (
            <div key={index} className={`svc-accordion-item ${isExpanded ? 'expanded' : ''}`}>
              <div className="svc-accordion-header" onClick={() => toggleService(index)}>
                <span className={`svc-chevron ${isExpanded ? 'open' : ''}`}>
                  {isExpanded ? <HiOutlineChevronDown size={16} /> : <HiOutlineChevronRight size={16} />}
                </span>
                <div className="svc-name-col">
                  <span className="svc-name">{cr.serviceName}</span>
                  <span className="svc-region">{cr.region}</span>
                </div>
                {info?.conditions?.length > 0 ? (() => {
                  const allReady = info.conditions.every(c => c.state === 'CONDITION_SUCCEEDED');
                  return allReady
                    ? <span className="status-badge healthy"><span className="status-dot"></span> Ready</span>
                    : <span className="status-badge error"><span className="status-dot"></span> Degraded</span>;
                })() : (
                  <span className="status-badge" style={{ background: 'var(--alpha-white-4)', color: 'var(--text-muted)' }}>
                    {isLoadingInfo ? '...' : '—'}
                  </span>
                )}
                <span className="svc-project-name">{cr.gcpProjectName || ''}</span>
                <button className="svc-remove-btn" onClick={(e) => handleRemoveService(e, index)} title="Remove service">
                  <HiOutlineTrash size={14} />
                </button>
              </div>

              {isExpanded && (
                <div className="svc-accordion-body">
                  {isLoadingInfo && (
                    <div className="loading-inline"><div className="spinner" style={{ width: 20, height: 20 }}></div><span>Loading service info...</span></div>
                  )}
                  {info && <ServiceDetail info={info} />}
                  {!isLoadingInfo && !info && (
                    <div className="svc-info-error">Could not load service info</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ServiceDetail({ info }) {
  return (
    <div className="svc-info-content">
      {/* Hero Banner */}
      <div className="svc-hero">
        <div className="svc-hero-left">
          <div className="svc-hero-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
          </div>
          <div className="svc-hero-text">
            <h3 className="svc-hero-name">{info.name}</h3>
            <div className="svc-hero-meta">
              <span className="svc-hero-badge region">{info.region}</span>
              <span className="svc-hero-badge ingress">{info.ingress?.replace('INGRESS_', '') || 'N/A'}</span>
            </div>
          </div>
        </div>
        {info.uri && (
          <a href={info.uri} target="_blank" rel="noopener noreferrer" className="svc-hero-url">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            <span>{info.uri}</span>
          </a>
        )}
      </div>

      {/* Detail Cards */}
      <div className="svc-detail-row">
        <div className="svc-detail-card">
          <div className="svc-dc-icon" style={{ color: '#3b82f6' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div className="svc-dc-info">
            <span className="svc-dc-label">Created</span>
            <span className="svc-dc-val">{formatDate(info.createTime)}</span>
          </div>
        </div>
        <div className="svc-detail-card">
          <div className="svc-dc-icon" style={{ color: '#8b5cf6' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </div>
          <div className="svc-dc-info">
            <span className="svc-dc-label">Last Updated</span>
            <span className="svc-dc-val">{formatDate(info.updateTime)}</span>
          </div>
        </div>
        <div className="svc-detail-card">
          <div className="svc-dc-icon" style={{ color: '#10b981' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div className="svc-dc-info">
            <span className="svc-dc-label">Creator</span>
            <span className="svc-dc-val truncate">{info.creator || 'N/A'}</span>
          </div>
        </div>
        <div className="svc-detail-card">
          <div className="svc-dc-icon" style={{ color: '#f59e0b' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div className="svc-dc-info">
            <span className="svc-dc-label">Last Modifier</span>
            <span className="svc-dc-val truncate">{info.lastModifier || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Revisions */}
      <div className="svc-section">
        <div className="svc-section-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
          <span>Revisions</span>
        </div>
        <div className="svc-rev-row">
          <div className="svc-rev-chip active">
            <span className="svc-rev-dot"></span>
            <div className="svc-rev-info">
              <span className="svc-rev-label">Ready</span>
              <span className="svc-rev-name">{info.latestReadyRevision?.split('/').pop() || 'N/A'}</span>
            </div>
          </div>
          <div className="svc-rev-chip">
            <span className="svc-rev-dot created"></span>
            <div className="svc-rev-info">
              <span className="svc-rev-label">Created</span>
              <span className="svc-rev-name">{info.latestCreatedRevision?.split('/').pop() || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Container Configuration */}
      {info.template && (
        <div className="svc-section">
          <div className="svc-section-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><circle cx="6" cy="6" r="1"/><circle cx="6" cy="18" r="1"/></svg>
            <span>Container Configuration</span>
          </div>
          <div className="svc-container-grid">
            {info.template.containers?.map((c, ci) => (
              <React.Fragment key={ci}>
                <div className="svc-cg-item wide">
                  <span className="svc-cg-label">Image</span>
                  <span className="svc-cg-val mono">{c.image || 'N/A'}</span>
                </div>
                <div className="svc-cg-item">
                  <span className="svc-cg-label">CPU</span>
                  <span className="svc-cg-val highlight blue">{c.resources?.limits?.cpu || 'Default'}</span>
                </div>
                <div className="svc-cg-item">
                  <span className="svc-cg-label">Memory</span>
                  <span className="svc-cg-val highlight purple">{c.resources?.limits?.memory || 'Default'}</span>
                </div>
                <div className="svc-cg-item">
                  <span className="svc-cg-label">Ports</span>
                  <span className="svc-cg-val highlight cyan">{c.ports?.map(p => p.containerPort).join(', ') || 'N/A'}</span>
                </div>
              </React.Fragment>
            ))}
            <div className="svc-cg-item">
              <span className="svc-cg-label">Scaling</span>
              <span className="svc-cg-val">{info.template.scaling ? `${info.template.scaling.minInstanceCount || 0} — ${info.template.scaling.maxInstanceCount || 'auto'}` : 'Default'}</span>
            </div>
            <div className="svc-cg-item">
              <span className="svc-cg-label">Timeout</span>
              <span className="svc-cg-val">{info.template.timeout ? `${info.template.timeout.seconds}s` : 'Default'}</span>
            </div>
            <div className="svc-cg-item">
              <span className="svc-cg-label">Concurrency</span>
              <span className="svc-cg-val">{info.template.maxInstanceRequestConcurrency || 'Default'}</span>
            </div>
            <div className="svc-cg-item wide">
              <span className="svc-cg-label">Service Account</span>
              <span className="svc-cg-val mono">{info.template.serviceAccount || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Conditions */}
      {info.conditions?.length > 0 && (
        <div className="svc-section">
          <div className="svc-section-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            <span>Conditions</span>
          </div>
          <div className="svc-conditions">
            {info.conditions.map((c, ci) => (
              <div key={ci} className={`svc-cond-card ${c.state === 'CONDITION_SUCCEEDED' ? 'success' : 'other'}`}>
                <div className="svc-cond-icon">
                  {c.state === 'CONDITION_SUCCEEDED' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  )}
                </div>
                <div className="svc-cond-text">
                  <span className="svc-cond-type">{c.type}</span>
                  <span className="svc-cond-state">{c.state?.replace('CONDITION_', '')}</span>
                </div>
                {c.message && c.message !== '-' && <span className="svc-cond-msg">{c.message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesTab;
